// @ts-strict-ignore
import React, { useEffect, useState } from 'react'

import { ReadyGateContainer as Container, AppMetaData, Language, convertLanguage } from 'mytours-read-common'

import { LanguageGate, selectAppLanguage, useOnboardingGate } from '../functions'
import { initAnalytics, AnalyticsProvider } from '../../logging/analytics'

import LanguageModal from 'mobile_web/modules/common/components/language-modal'
import FormModal from 'mobile_web/modules/common/components/form-modal'

import SettingsService, { DEFAULT_APP_INFO, loadDefaultLanguageCode } from 'mobile_web/modules/settings/functions'
import { ionHistory } from 'mobile_web/modules/navigation'
import { LanguageCode } from 'mytours-read-api-typescript'
import DefaultPagePlaceholder from 'mobile_web/modules/common/components/placeholders/DefaultPage'
import usePrevious from '../../../common_lib/hooks/use-previous'
import HomeComponent from './Home'
import { getLanguageCodeFromUrl, replaceLanguageCodeInPathname } from 'mobile_web/modules/navigation/utils'

const FIFTEEN_MINUTES_IN_MS: number = 15 * 60 * 1000

const ReadyGate: React.FC<Container.Props & Container.Actions> = props => {
	const [updating, setUpdating] = useState<boolean>(false)
	const [storedLanguage, setStoredLanguage] = useState<LanguageCode>()
	const [loading, setLoading] = useState<boolean>(true)
	const [isManifestLoading, setManifestLoading] = useState<boolean>(false)

	const prevPropsAppMetaData: AppMetaData = usePrevious(props.appMetaData)
	const prevPropsReady: boolean = usePrevious(props.ready)

	const prevStateStoredLanguage: LanguageCode = usePrevious(storedLanguage)

	const onboardingGate = useOnboardingGate((id: number) => {
		props.loadForm(id, 0, props.selectedLanguage?.code || 'en')
	})

	const selectLanguage = (language: Language) => {
		const { appMetaData } = props
		if (!appMetaData) {
			return
		}

		ionHistory.replace(replaceLanguageCodeInPathname(appMetaData.supported_languages, language.code as LanguageCode)) // TODO: add LanguageCode enum into Language interface
		props.selectLanguage(language)
		SettingsService.appLanguage.setCurrentLocale(language.code)
	}

	const getStoredLanguage = async () => {
		const { appMetaData, selectedLanguage } = props

		if (!appMetaData) {
			return
		}

		const storedLanguageTmp = await loadDefaultLanguageCode(DEFAULT_APP_INFO.projectId)

		if (storedLanguageTmp && !appMetaData.supported_languages.includes(storedLanguageTmp)) {
			setStoredLanguage(appMetaData.primary_language)
			setLoading(true)
			return
		}

		if (!loading) {
			return
		}

		if (!storedLanguageTmp && selectedLanguage === undefined) {
			const languageParam = getLanguageCodeFromUrl(appMetaData.supported_languages)
			if (languageParam) {
				selectLanguage(convertLanguage(languageParam))
			}
		} else if (storedLanguageTmp && selectedLanguage === undefined) {
			setStoredLanguage(storedLanguageTmp)
			props.selectLanguage(convertLanguage(storedLanguageTmp))
			SettingsService.appLanguage.setCurrentLocale(storedLanguageTmp)
		} else if (storedLanguageTmp && prevStateStoredLanguage !== storedLanguageTmp) {
			setStoredLanguage(storedLanguageTmp)
		}

		setLoading(false)
	}

	useEffect(() => {
		LanguageGate.init(props.selectLanguage)
		initAnalytics()

		setInterval(() => {
			props.checkForUpdates()
		}, FIFTEEN_MINUTES_IN_MS)
	}, [])

	useEffect(() => {
		const { search } = ionHistory.location
		if (search) {
			const demoEnabled = new URLSearchParams(search).get('demo')
			sessionStorage.setItem('demo_mode', `${demoEnabled}`)
		}
	}, [])

	useEffect(() => {
		if (!prevPropsReady) {
			return
		}

		if (prevPropsAppMetaData && props.appMetaData && prevPropsAppMetaData.updated_at !== props.appMetaData.updated_at) {
			setUpdating(true)
			setUpdating(false)
		}
	}, [prevPropsAppMetaData, props.appMetaData])

	useEffect(() => {
		if (!props.ready) {
			// if we do not have meta data (meta data was not saved to redux persist)
			if (!props.appMetaData) {
				props.loadAppMetaData()
			}
		}
	}, [props.ready, props.appMetaData])

	useEffect(() => {
		if (props.appMetaData?.onboarding_steps && !props.selectedLanguage) {
			// select default language if no language is selected
			selectAppLanguage(props.appMetaData, undefined)
		}
	}, [props.appMetaData?.onboarding_steps, props.selectedLanguage])

	useEffect(() => {
		// Init onboarding gate
		if (props.appMetaData?.onboarding_steps && props.selectedLanguage)
			onboardingGate.init(props.appMetaData.onboarding_steps)
	}, [props.appMetaData?.onboarding_steps, props.selectedLanguage])

	useEffect(() => {
		if (props.ready && props.appMetaData && Object.keys(props.appManifest).length === 0 && !isManifestLoading) {
			props.downloadManifest()
			setManifestLoading(true)
		}
	}, [props.ready, props.appMetaData, props.appManifest])

	useEffect(() => {
		if (isManifestLoading && Object.keys(props.appManifest).length > 0) {
			setManifestLoading(false)
		}
	}, [isManifestLoading])

	useEffect(() => {
		getStoredLanguage()
	}, [props.appMetaData, props.selectedLanguage, loading])

	// check if we have meta data loaded
	if (props.ready && props.appMetaData && !updating) {
		// show language gate
		if (
			!loading &&
			!storedLanguage &&
			props.selectedLanguage === undefined &&
			props.appMetaData.supported_languages.length > 1
		) {
			return (
				<LanguageModal
					isOpen={true}
					showBackdrop={false}
					backdropDismiss={false}
					selectLanguage={selectLanguage}
					supportedLanguages={props.appMetaData.supported_languages}
				/>
			)
		}

		if (onboardingGate.loaded) {
			// Show onboarding step if gated
			if (!onboardingGate.done) {
				const form = props.forms[onboardingGate.incompleteSteps[0].form_id].item
				if (!form) {
					return null
				}

				return (
					<FormModal
						isOpen={true}
						showBackdrop={false}
						backdropDismiss={false}
						form={form}
						skippable={!onboardingGate.incompleteSteps[0].gated}
						onFinish={() => onboardingGate.completeStep(onboardingGate.incompleteSteps[0].id)}
					/>
				)
			}

			return (
				<AnalyticsProvider>
					<HomeComponent {...props} />
				</AnalyticsProvider>
			)
		}
	}

	// else show loading
	return <DefaultPagePlaceholder />
}

const ReadyGateComponent = Container.default(ReadyGate)
export default ReadyGateComponent
